import { useEffect, useState } from 'react';

import { LandingScreen } from '../screens/Landing/LandingScreen';
import { GuestHelpScreen } from '../screens/GuestHelp/GuestHelpScreen';
import { PublicMaterialsScreen, type PublicMaterial } from '../screens/GuestHelp/PublicMaterialsScreen';
import { RequestWizardScreen } from '../screens/RequestWizard/RequestWizardScreen';
import { StandaloneAuthScreen } from '../screens/StandaloneAuth/StandaloneAuthScreen';
import { CarrierRegistrationScreen, type CarrierRegistrationScreenId } from '../screens/CarrierRegistration/CarrierRegistrationScreen';
import { CarrierVerificationScreen, type CarrierVerificationScreenId } from '../screens/CarrierVerification/CarrierVerificationScreen';
import { CarrierCabinetScreen, type ImplementedCarrierCabinetScreenId } from '../screens/CarrierCabinet/CarrierCabinetScreen';
import { CarrierOfferScreen, type CarrierOfferScreenId } from '../screens/CarrierCabinet/CarrierOfferScreen';
import { CarrierChatScreen, type CarrierChatScreenId } from '../screens/CarrierCabinet/CarrierChatScreen';
import { CarrierWorkScreen, type CarrierWorkScreenId } from '../screens/CarrierCabinet/CarrierWorkScreen';
import { CarrierSupportScreen, type CarrierSupportScreenId } from '../screens/CarrierCabinet/CarrierSupportScreen';
import { CarrierProfileScreen, type CarrierProfileScreenId } from '../screens/CarrierProfile/CarrierProfileScreen';
import { CustomerRequestsScreen, type CustomerRequestScreenId } from '../screens/CustomerCabinet/CustomerRequestsScreen';
import { CustomerMessagesScreen, type CustomerMessageScreenId } from '../screens/CustomerCabinet/CustomerMessagesScreen';
import { CustomerAccountScreen, type CustomerAccountScreenId } from '../screens/CustomerCabinet/CustomerAccountScreen';
import { CustomerProfileScreen, type CustomerProfileScreenId } from '../screens/CustomerCabinet/CustomerProfileScreen';
import { PlatformFeedbackScreen } from '../screens/CustomerCabinet/PlatformFeedbackScreen';
import { normalizeScreenId, type ScreenId } from './screenRoutes';
import { useLanguage } from '../i18n/LanguageProvider';
import { useAuth } from '../state/auth';

const REQUEST_WIZARD_SCREENS = new Set<ScreenId>([
  'new-request-step-1',
  'new-request-step-2',
  'new-request-step-3',
  'new-request-step-4',
  'new-request-step-5',
  'new-request-step-6',
  'new-request-step-7',
  'new-request-step-8',
  'new-request-step-9',
  'new-request-step-9-role',
  'new-request-step-9-name',
  'new-request-step-9-name-company',
  'new-request-step-9-sent',
  'new-request-step-9-password',
  'new-request-step-10',
]);

const STANDALONE_AUTH_SCREENS = new Set<ScreenId>([
  'login',
  'forgot-password',
  'forgot-password-sent',
  'reset-password',
  'reset-password-success',
  'email-confirm',
  'registration-role',
  'customer-rega-email',
  'customer-rega-type',
  'customer-rega-contact',
  'customer-rega-contact-company',
  'customer-rega-sent',
  'customer-rega-password',
  'customer-rega-success',
]);

const CARRIER_REGISTRATION_SCREENS = new Set<ScreenId>([
  'carrier-rega-email',
  'carrier-rega-type',
  'carrier-rega-contact',
  'carrier-rega-contact-company',
  'carrier-rega-sent',
  'carrier-rega-password',
]);

const CARRIER_VERIFICATION_SCREENS = new Set<ScreenId>([
  'carrier-verify',
  'carrier-verify-pending',
]);

const CARRIER_CABINET_SCREENS = new Set<ScreenId>([
  'carrier-dashboard',
  'carrier-search',
  'carrier-request-detail',
  'carrier-notifications',
]);

const CARRIER_OFFER_SCREENS = new Set<ScreenId>([
  'carrier-offer',
  'carrier-my-offers',
]);

const CARRIER_CHAT_SCREENS = new Set<ScreenId>([
  'carrier-chat-list',
  'carrier-chat',
]);

const CARRIER_WORK_SCREENS = new Set<ScreenId>([
  'carrier-active-transport',
  'carrier-completed',
  'carrier-earnings',
]);

const CARRIER_SUPPORT_SCREENS = new Set<ScreenId>([
  'carrier-review',
  'carrier-dispute',
  'carrier-help',
  'carrier-documents',
  'carrier-settings',
]);

const CARRIER_PROFILE_SCREENS = new Set<ScreenId>([
  'carrier-profile-internal',
  'carrier-profile-external',
]);

const CUSTOMER_REQUEST_SCREENS = new Set<ScreenId>([
  'my-requests',
  'request-detail',
  'payment',
  'active-transport',
]);

const CUSTOMER_MESSAGE_SCREENS = new Set<ScreenId>([
  'chat-list',
  'chat',
]);

const CUSTOMER_ACCOUNT_SCREENS = new Set<ScreenId>([
  'review',
  'dispute',
  'notifications',
  'settings',
  'customer-help',
]);

const CUSTOMER_PROFILE_SCREENS = new Set<ScreenId>([
  'profile-internal',
  'profile-external',
]);

function currentHash(): string {
  return window.location.hash;
}

function PublicLandingEntry() {
  useEffect(() => {
    if (window.location.pathname.startsWith('/portal')) {
      window.location.replace('/#landing');
    }
  }, []);

  if (window.location.pathname.startsWith('/portal')) return null;
  return <LandingScreen />;
}

function CustomerRoleRequired() {
  useEffect(() => { window.location.hash = 'carrier-settings?intent=create-request'; }, []);
  return null;
}

export function App() {
  useLanguage();
  const [hash, setHash] = useState<string>(currentHash);
  const screen = normalizeScreenId(hash);

  useEffect(() => {
    const onHashChange = () => setHash(currentHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const auth = useAuth();
  let resolvedScreen = screen;
  const isCarrier = auth.user?.active_role === 'carrier' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('dev') === 'carrier');

  // profile-external with an explicit target (?customer=N / ?id=N) is a link to a
  // specific customer's public profile and must not be remapped for carriers.
  const externalProfileQuery = new URLSearchParams(hash.split('?')[1] ?? '');
  const hasExternalProfileTarget = externalProfileQuery.has('customer') || externalProfileQuery.has('id');

  if (isCarrier) {
    if (screen === 'profile-internal') resolvedScreen = 'carrier-profile-internal';
    if (screen === 'profile-external' && !hasExternalProfileTarget) resolvedScreen = 'carrier-profile-external';
    if (screen === 'chat-list') resolvedScreen = 'carrier-chat-list';
    if (screen === 'notifications') resolvedScreen = 'carrier-notifications';
    if (screen === 'active-transport') resolvedScreen = 'carrier-active-transport';
    if (screen === 'settings') resolvedScreen = 'carrier-settings';
  }
  // A role switch can render before the hashchange event reaches React.
  if (auth.user?.active_role === 'customer' && screen === 'carrier-settings') resolvedScreen = 'settings';

  if (resolvedScreen === 'landing') return <PublicLandingEntry />;
  if (resolvedScreen === 'guest-help') return <GuestHelpScreen />;
  if (['privacy-policy', 'public-offer', 'service-rules', 'social-links'].includes(resolvedScreen)) return <PublicMaterialsScreen material={resolvedScreen as PublicMaterial} />;
  if (REQUEST_WIZARD_SCREENS.has(resolvedScreen)) {
    if (auth.status === 'loading') return null;
    if (auth.user?.active_role === 'carrier') return <CustomerRoleRequired />;
    return <RequestWizardScreen screen={resolvedScreen} />;
  }
  if (STANDALONE_AUTH_SCREENS.has(resolvedScreen)) return <StandaloneAuthScreen screen={resolvedScreen as Parameters<typeof StandaloneAuthScreen>[0]['screen']} />;
  if (CARRIER_REGISTRATION_SCREENS.has(resolvedScreen)) return <CarrierRegistrationScreen screen={resolvedScreen as CarrierRegistrationScreenId} />;
  if (CARRIER_VERIFICATION_SCREENS.has(resolvedScreen)) return <CarrierVerificationScreen screen={resolvedScreen as CarrierVerificationScreenId} />;
  if (CARRIER_CABINET_SCREENS.has(resolvedScreen)) return <CarrierCabinetScreen screen={resolvedScreen as ImplementedCarrierCabinetScreenId} />;
  if (CARRIER_OFFER_SCREENS.has(resolvedScreen)) return <CarrierOfferScreen screen={resolvedScreen as CarrierOfferScreenId} />;
  if (CARRIER_CHAT_SCREENS.has(resolvedScreen)) return <CarrierChatScreen screen={resolvedScreen as CarrierChatScreenId} />;
  if (CARRIER_WORK_SCREENS.has(resolvedScreen)) return <CarrierWorkScreen screen={resolvedScreen as CarrierWorkScreenId} />;
  if (CARRIER_SUPPORT_SCREENS.has(resolvedScreen)) return <CarrierSupportScreen screen={resolvedScreen as CarrierSupportScreenId} />;
  if (CARRIER_PROFILE_SCREENS.has(resolvedScreen)) return <CarrierProfileScreen screen={resolvedScreen as CarrierProfileScreenId} />;
  if (CUSTOMER_REQUEST_SCREENS.has(resolvedScreen)) return <CustomerRequestsScreen screen={resolvedScreen as CustomerRequestScreenId} />;
  if (CUSTOMER_MESSAGE_SCREENS.has(resolvedScreen)) return <CustomerMessagesScreen screen={resolvedScreen as CustomerMessageScreenId} />;
  if (CUSTOMER_ACCOUNT_SCREENS.has(resolvedScreen)) return <CustomerAccountScreen screen={resolvedScreen as CustomerAccountScreenId} />;
  if (CUSTOMER_PROFILE_SCREENS.has(resolvedScreen)) return <CustomerProfileScreen screen={resolvedScreen as CustomerProfileScreenId} />;
  if (resolvedScreen === 'platform-feedback') return <PlatformFeedbackScreen />;

  // Unmigrated routes stay intentionally blank in v2 until their exact Figma
  // frame is attached. Production continues to serve v1 during this phase.
  return null;
}
